/* Wave 2 — Research report 14-section detail view.
 *
 * Renders each of the 14 sections as a shadcn Card with:
 *   - collapse toggle
 *   - "Section regenerieren" button that POSTs to
 *     /research/reports/{id}/regenerate-section
 *   - optimistic update on success
 *
 * German UI copy; English code.
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ChevronDown, ChevronUp, RefreshCw, AlertCircle } from "lucide-react";

const SECTION_ORDER = [
  ["meta_summary", "Executive Summary"],
  ["market_overview", "Marktüberblick"],
  ["target_audience", "Zielgruppe"],
  ["competitors", "Wettbewerber"],
  ["differentiation_opportunities", "Differenzierungs-Chancen"],
  ["winning_ad_angles", "Winning Ad-Angles"],
  ["psychological_triggers", "Psychologische Trigger"],
  ["objections", "Einwände"],
  ["industry_language", "Branchen-Sprache"],
  ["content_opportunities", "Content-Chancen"],
  ["search_intent", "Suchintent"],
  ["trends", "Trends"],
  ["regional_context", "Regionaler Kontext"],
  ["compliance_risks", "Compliance-Risiken"],
];

export default function ResearchSectionsView({ report, onSectionUpdated }) {
  if (!report?.sections) return null;
  return (
    <div className="space-y-4" data-testid="research-sections-view">
      <div className="flex items-center gap-2 text-xs text-muted-foreground"
              data-testid="research-report-meta">
        <span>Quelle: <strong>{report.source}</strong></span>
        <span>·</span>
        <span>Status: <strong>{report.status}</strong></span>
        <span>·</span>
        <span>Kosten: <strong>${(report.total_cost_usd || 0).toFixed(4)}</strong></span>
        <span>·</span>
        <span>Latency: <strong>{report.total_latency_ms || 0}ms</strong></span>
        {report.fallback_reason && (
          <>
            <span>·</span>
            <span className="text-amber-700 dark:text-amber-400">
              Fallback: {report.fallback_reason}
            </span>
          </>
        )}
      </div>
      {SECTION_ORDER.map(([key, title]) => (
        <SectionCard
          key={key}
          section={key}
          title={title}
          value={report.sections[key]}
          reportId={report.id}
          regenUsed={(report.section_regenerations_used || {})[key] || 0}
          onUpdated={(newVal, used) => onSectionUpdated?.(key, newVal, used)}
        />
      ))}
    </div>
  );
}

function SectionCard({ section, title, value, reportId, regenUsed, onUpdated }) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [used, setUsed] = useState(regenUsed);
  const remaining = Math.max(0, 3 - used);

  const regenerate = async () => {
    if (remaining === 0) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/research/reports/${reportId}/regenerate-section`,
        { section_name: section });
      const body = r.data;
      if (body.cap_reached) {
        toast.warning("Maximale Neugenerierungen (3) für diesen Abschnitt erreicht");
        setUsed(3);
        return;
      }
      if (!body.updated) {
        toast.error(`Abschnitt konnte nicht neu generiert werden (${body.reason || "unknown"})`);
        return;
      }
      setUsed(body.regenerations_used || used + 1);
      onUpdated?.(body.new_value, body.regenerations_used);
      toast.success(`Abschnitt „${title}" neu generiert`);
    } catch (e) {
      toast.error("Fehler beim Regenerieren");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid={`research-section-${section}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm">{title}</CardTitle>
          <Badge variant="outline" className="text-[10px]">{section}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">
            Neu generieren: {remaining}/3
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={regenerate}
            disabled={busy || remaining === 0}
            data-testid={`research-regen-${section}`}
          >
            <RefreshCw className={"size-3 mr-1 " + (busy ? "animate-spin" : "")} />
            Regenerieren
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setOpen(!open)}
            data-testid={`research-toggle-${section}`}
          >
            {open ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          </Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="text-xs pt-2"
                          data-testid={`research-content-${section}`}>
          <SectionBody value={value} />
        </CardContent>
      )}
    </Card>
  );
}

function SectionBody({ value }) {
  if (value == null || value === "") {
    return <div className="text-muted-foreground flex items-center gap-1">
      <AlertCircle className="size-3" /> Leer
    </div>;
  }
  if (typeof value === "string") {
    return <p className="whitespace-pre-wrap leading-relaxed">{value}</p>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <div className="text-muted-foreground">Keine Einträge</div>;
    }
    return (
      <div className="space-y-2">
        {value.map((item, i) => (
          <div key={i} className="border-l-2 border-primary/40 pl-2">
            {typeof item === "string" ? (
              <span>{item}</span>
            ) : (
              <ObjectRender obj={item} />
            )}
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === "object") {
    return <ObjectRender obj={value} />;
  }
  return <span>{String(value)}</span>;
}

function ObjectRender({ obj }) {
  const entries = Object.entries(obj || {});
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-[minmax(140px,auto)_1fr] gap-x-3 gap-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-[10px] uppercase tracking-wider text-muted-foreground pt-0.5">
            {k.replace(/_/g, " ")}
          </dt>
          <dd className="text-xs">
            {Array.isArray(v)
              ? v.length === 0
                ? <span className="text-muted-foreground">—</span>
                : (
                  <ul className="list-disc pl-4 space-y-0.5">
                    {v.map((x, i) => (
                      <li key={i}>
                        {typeof x === "string" ? x : JSON.stringify(x)}
                      </li>
                    ))}
                  </ul>
                )
              : typeof v === "object" && v !== null
                ? <pre className="text-[10px] bg-muted/30 rounded p-1 overflow-x-auto">
                    {JSON.stringify(v, null, 2)}
                  </pre>
                : <span>{String(v ?? "—")}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function ExportButton({ reportId }) {
  const [busy, setBusy] = useState(false);
  const download = async (format) => {
    setBusy(true);
    try {
      const r = await api.post(
        `/research/reports/${reportId}/export`,
        { format });
      const body = r.data;
      if (body.format === "pdf" && body.signed_url) {
        window.open(body.signed_url, "_blank");
      } else {
        // Markdown inline → trigger client-side download.
        const blob = new Blob([body.content || ""],
                                    { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = body.filename || `research-${reportId}.md`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error("Export fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex gap-2" data-testid="research-export">
      <Button size="sm" variant="outline" onClick={() => download("markdown")}
                  disabled={busy} data-testid="research-export-md">
        .md
      </Button>
      <Button size="sm" variant="outline" onClick={() => download("pdf")}
                  disabled={busy} data-testid="research-export-pdf">
        .pdf
      </Button>
    </div>
  );
}
